import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function CheshireEastCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Cheshire East Pothole Claims | 126,545 Repairs in Five Years | Fixtyer"
        description="Cheshire East Council filled a record 37,766 potholes in 2024/25 and projects £53m capital spend with a GREEN DfT rating — yet condition is AMBER, B/C roads sit at 35% amber-or-worse, and U-road RED hit 13%. See what this means for your pothole claim."
        canonical="/councils/cheshire-east"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Cheshire East', url: '/councils/cheshire-east' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Cheshire East has a GREEN DfT Spend rating and projects £53m capital spend — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is road condition — and Cheshire East is AMBER overall. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate. The council itself links preventative maintenance to reducing insurance claims, which confirms it knows reactive patching does not eliminate liability.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads are surveyed using Coarse Visual Inspection (CVI) across 100% of the network over a 2-year period — so any given U-road may go a full year between condition surveys. Cheshire East\'s own report warns U-road data "experiences more annual fluctuations" because there is no standardised national method for unclassified roads. RED-condition U-roads peaked at 13% in 2022 and 2023 before falling to 11% in 2024.',
          },
          {
            question: 'Only half of C roads are SCANNER-surveyed each year — does that help my claim?',
            answer: 'Potentially. Cheshire East carries out SCANNER surveys on 100% of B roads and 50% of C roads over a 2-year cycle. If your pothole was on a C road, ask whether that stretch was surveyed in the year of your incident — and what action followed a RED or amber classification. B and C roads have sat at 5% RED and 30% amber since 2024, meaning a third of the classified local network is flagged for maintenance now or soon.',
          },
          {
            question: 'Cheshire East filled 37,766 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. A record pothole year is evidence of a network producing defects faster than planned maintenance prevents them. The five-year total is 126,545 pothole repairs, and the council states approximately 20% of its reactive spend goes on potholes every year. Reactive maintenance "may seem less expensive at first" but "can lead to higher costs due to frequent repairs" — the council\'s own words.',
          },
          {
            question: 'Does Best Practice AMBER weaken or strengthen the council\'s Section 58 defence?',
            answer: 'Cheshire East follows the Well Managed Highway Infrastructure code, uses asset-management-led programming, and has tested artificial intelligence to identify potholes — all of which support a documented Section 58 defence. But Best Practice is AMBER, not GREEN, and the council\'s 2024/25 reactive share jumped to 25% of maintenance spending. Your claim still turns on the specific defect, prior reports, and photos — not the council\'s strategy documents alone.',
          },
          {
            question: 'Reactive spending rose to 25% in 2024/25 — what does that mean for my claim?',
            answer: 'For the four prior years, Cheshire East\'s reactive share sat at 15–18%. In 2024/25 it rose to 25% — the highest in the published five-year table — while pothole repairs hit a record 37,766. That is documented shift from prevention towards patching, which is useful context when arguing the council was fighting a losing reactive battle on the road where you were damaged.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Cheshire East Council"
        councilSlug="cheshire-east"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/cheshire-east"
        headline="Cheshire East Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Cheshire East</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Cheshire East: GREEN Spend, Record Pothole Year
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Cheshire East projects <strong className="text-emerald-400">£53m</strong> of capital highway spend in 2025/26
            against a <strong className="text-emerald-400">£38.6m</strong> DfT allocation, with 86% classed as preventative.
            Yet the overall DfT rating is <strong className="text-amber-400">AMBER</strong> — because the council repaired
            <strong className="text-rose-400"> 126,545 potholes in five years</strong>, hit a record{' '}
            <strong className="text-rose-400">37,766</strong> in 2024/25 alone, and a third of B and C roads sit in amber
            or RED condition.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">37,766</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25 — a five-year high</div>
            <div className="text-brand-300 text-sm mt-1">
              More than 103 pothole repairs every day across Cheshire East's 2,700km carriageway network —
              up from 15,637 in 2020/21.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network & survey coverage */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A 2,700km Network With Uneven Survey Coverage
          </h2>
          <p className="text-surface-600 mb-8">
            Cheshire East's transparency report publishes total carriageway length and survey methodology by road class —
            not a kilometre split between A, B, C and unclassified roads
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">2,700km+</div>
              <div className="text-sm text-surface-600 mt-2">Carriageway maintained</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">100%</div>
              <div className="text-sm text-surface-600 mt-2">A-roads SCANNER-surveyed annually</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-amber-600">50%</div>
              <div className="text-sm text-surface-600 mt-2">C-roads surveyed per year (2-year cycle)</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Survey method</th>
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Coverage cycle</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Classified Principal A</td>
                  <td className="px-6 py-4">SCANNER</td>
                  <td className="px-6 py-4">100% over 1 year</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Classified Non-Principal B</td>
                  <td className="px-6 py-4">SCANNER</td>
                  <td className="px-6 py-4">100% over 1 year</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Classified Non-Principal C</td>
                  <td className="px-6 py-4">SCANNER</td>
                  <td className="px-6 py-4 text-amber-800 font-medium">50% over 2 years</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Unclassified (U)</td>
                  <td className="px-6 py-4">Coarse Visual Inspection (CVI)</td>
                  <td className="px-6 py-4 text-amber-800 font-medium">100% over 2 years</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why Survey Gaps Matter For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Cheshire East must maintain its roads. Under Section 58,
              it can defend a claim by showing it had a reasonable system for inspecting and repairing defects.
              The council's own published survey cycles create questions:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your C road surveyed in the year your pothole formed, or the off year?</li>
              <li>• On U-roads, was your street condition-assessed in the year of your incident?</li>
              <li>• If B/C roads are 35% amber-or-worse, what was done about the stretch where you were damaged?</li>
              <li>• Safety inspections and condition surveys are different — ask which log applies to your claim</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim comprehensive network knowledge while surveying half its C roads and all its
              U-roads on multi-year cycles.
            </p>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of condition data from Cheshire East's own transparency report — A-roads improving,
            B/C amber band widening, U-road RED peaking at 13%
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads: improving on SCANNER data</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 5% in 2021</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">27%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 32% in 2021</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">70%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 63% in 2021</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Main roads are the bright spot. But principal A-roads are a minority of the 2,700km network,
              and a GREEN A-road scorecard does not help you if your pothole was on a residential U-road.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads: stuck at 5% RED, amber band widening</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">33%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">30%</td>
                    <td className="px-4 py-2 text-center font-bold">65%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED has sat at 5% for four consecutive years, but the amber band has widened from 27% to 30%
              since 2022 — and green has fallen from 68% to 65%. Combined, 35% of B and C roads are flagged
              for maintenance now or soon.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <h3 className="font-semibold text-brand-900 px-6 pt-6 mb-4">Unclassified roads: RED category only (CVI data)</h3>
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold">11%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="It's important to note that the data for the unclassified road network experiences more annual fluctuations compared to other road indicators. This is because there is no standardised method for Local Authorities to collect road condition data for unclassified roads. Therefore, caution should be exercised when comparing Local Authorities due to variations in survey methods and coverage."
            source="Cheshire East Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend — With A Reactive Spike
          </h2>
          <p className="text-surface-600 mb-8">
            Cheshire East invests well beyond its DfT allocation, but 2024/25 saw reactive maintenance jump to 25%
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">17,015,000</td>
                  <td className="px-4 py-3 text-center">19,269,951</td>
                  <td className="px-4 py-3 text-center">10,102,733</td>
                  <td className="px-4 py-3 text-center">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">13,048,000</td>
                  <td className="px-4 py-3 text-center">16,338,167</td>
                  <td className="px-4 py-3 text-center">10,755,191</td>
                  <td className="px-4 py-3 text-center">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">13,048,000</td>
                  <td className="px-4 py-3 text-center">20,413,000</td>
                  <td className="px-4 py-3 text-center">10,953,536</td>
                  <td className="px-4 py-3 text-center">82%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">15,367,600</td>
                  <td className="px-4 py-3 text-center">21,867,600</td>
                  <td className="px-4 py-3 text-center">10,674,557</td>
                  <td className="px-4 py-3 text-center">85%</td>
                  <td className="px-4 py-3 text-center">15%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-4 py-3 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-3 text-center">13,048,000</td>
                  <td className="px-4 py-3 text-center">19,477,442</td>
                  <td className="px-4 py-3 text-center font-bold">12,360,357</td>
                  <td className="px-4 py-3 text-center">75%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">25%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">38,640,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">53,031,000</td>
                  <td className="px-4 py-3 text-center">12,575,936</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">The GREEN Spend Story</h3>
              <p className="text-emerald-800 text-sm mb-3">
                Cheshire East consistently tops up DfT capital with locally resourced investment. For 2025/26 it
                projects £53.031m total capital against £38.64m allocated — roughly 37% above the DfT figure —
                with 86% classed as preventative.
              </p>
              <p className="text-emerald-800 text-sm">
                Over five years it has also treated approximately 185km of carriageway through capital programmes,
                upgraded roughly 250 bridges and structures, and upgraded around 2,000 streetlights.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2024/25 Reactive Spike</h3>
              <p className="text-amber-800 text-sm mb-3">
                Reactive maintenance jumped to 25% in 2024/25 — up from 15% the year before — while revenue spend
                rose to £12.36m, the highest in the published table. That is the same year pothole repairs hit
                37,766.
              </p>
              <p className="text-amber-800 text-sm">
                The council states approximately 20% of reactive spend goes on pothole repairs annually.
                More patching, not fewer defects.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Research indicates that letting an asset deteriorate to a poor condition (red) before replacing it is the most expensive and unsustainable option over time. The best approach is to keep the network in a stable condition by implementing preventive measures for assets that are in the upper green and amber stages of their lifecycle."
            source="Cheshire East Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            126,545 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">15,637</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">27,464</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">20,076</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">25,602</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">37,766</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">126,545</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <QuoteCard
              quote="Approximately 20% of the authority's reactive spend per year is spent on pothole repairs. A total of approximately 126,545 potholes have been fixed over the last 5 years."
              source="Cheshire East Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="While reactive maintenance may seem less expensive at first, it can lead to higher costs due to frequent repairs and unexpected downtime."
              source="Cheshire East Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">~69 Pothole Repairs a Day, Averaged Over Five Years</h3>
            <p className="text-brand-200 mb-4">
              A network producing potholes at that rate is, by definition, a network where defects routinely form
              between inspections — exactly the scenario where prior reports and photographic evidence decide claims
              under Section 58.
            </p>
            <p className="text-white font-medium">
              In 2024/25 alone the rate exceeded 103 repairs per day. Record patching is not record prevention.
            </p>
          </div>
        </div>
      </section>

      {/* Forward plans & AI */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Plans, AI Detection and Drainage Risk
          </h2>
          <p className="text-surface-600 mb-8">
            What Cheshire East says it will do in 2025/26 — and what it already knows about network vulnerability
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Planned Works</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Approximately 61km of preventative carriageway maintenance</li>
                <li>• Around 2.5km of resurfacing across the network</li>
                <li>• Capital funding prioritised on preventative maintenance</li>
                <li>• Rolling programmes published on the highway maintenance investment programme webpage</li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                63.5km of planned carriageway treatment on a 2,700km network is roughly 2.4% of total length.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Innovation and Climate Admissions</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council has tested artificial intelligence to identify asset condition and potholes, and uses
                systems such as Triopsis to programme works. That is evidence it can prioritise defects — which
                raises questions when a specific pothole was missed.
              </p>
              <p className="text-amber-800 text-sm">
                It also states network drainage is "particularly vulnerable to the impacts of climate change" and
                is prioritising drainage asset maintenance over multiple years.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The capital funding will continue to be prioritised on preventative maintenance with the aim to reduce the number of defects appearing, improving condition across the highway network and helping to reduce insurance claims made against the Council."
              source="Cheshire East Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="We have tested and utilised artificial intelligence to identify asset condition and potholes, enabling us to prioritise suitable improvement projects. It has also helped us programme our works more efficiently daily through systems such as Triopsis."
              source="Cheshire East Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="We recognise that our network drainage is particularly vulnerable to the impacts of climate change."
              source="Cheshire East Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 58 on Streetworks Routes</h3>
            <p className="text-brand-200 mb-4">
              Cheshire East puts Section 58 restrictions in place under the New Roads and Street Works Act 1991 after
              major carriageway works — protecting a street from planned street works for up to five years. If your
              pothole formed after recent resurfacing on a protected street, the council had heightened reason to
              expect the surface to hold.
            </p>
            <p className="text-white font-medium">
              Conversely, if utility streetworks preceded your damage, the permit scheme and Section 58 records are
              worth requesting.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Cheshire East is not a RED-rated authority — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projects £53m capital against £38.6m DfT allocation in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 86% projected preventative share for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition improving — 70% green in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management, AI pothole detection, and WMHI-aligned inspection hierarchy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% annual SCANNER coverage on A and B roads
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on principal A-roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 35% of B/C roads amber-or-worse, U-road RED peaked at 13%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> C-roads only 50% SCANNER-surveyed each year; U-roads on 2-year CVI cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 126,545 pothole repairs in five years — record 37,766 in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive share jumped to 25% in 2024/25 — documented shift to patching
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits U-road condition data fluctuates and is not nationally standardised
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Best Practice AMBER — not the top DfT tier
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and documented asset management, your claim lives or dies on the
              <strong className="text-white"> specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a C or U road, the multi-year survey gap is your strongest structural argument</li>
              <li>• Whether 2024/25 reactive spike and record pothole count correlates with your road's condition band</li>
              <li>• Drainage failure context if water pooling contributed to the defect</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Cheshire East's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Cheshire East?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No C-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No 2024/25 reactive spike cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 126,545 repairs in five years documented</li>
                <li>✅ C/U-road survey-cycle gaps argued</li>
                <li>✅ B/C amber-band decline cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Cheshire East</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=cheshire-east"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Cheshire East Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Cheshire East has a GREEN DfT Spend rating and projects £53m capital spend — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is road condition —
                and Cheshire East is AMBER overall. Section 58 turns on whether the specific defect that damaged your
                vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate. The
                council itself links preventative maintenance to reducing insurance claims, which confirms it knows
                reactive patching does not eliminate liability.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads are surveyed using Coarse Visual Inspection (CVI) across 100% of the network over a
                2-year period — so any given U-road may go a full year between condition surveys. Cheshire East's own
                report warns U-road data "experiences more annual fluctuations" because there is no standardised
                national method for unclassified roads. RED-condition U-roads peaked at 13% in 2022 and 2023 before
                falling to 11% in 2024.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Only half of C roads are SCANNER-surveyed each year — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially. Cheshire East carries out SCANNER surveys on 100% of B roads and 50% of C roads over a
                2-year cycle. If your pothole was on a C road, ask whether that stretch was surveyed in the year of
                your incident — and what action followed a RED or amber classification. B and C roads have sat at 5%
                RED and 30% amber since 2024, meaning a third of the classified local network is flagged for
                maintenance now or soon.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Cheshire East filled 37,766 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. A record pothole year is evidence of a network producing defects faster than planned maintenance
                prevents them. The five-year total is 126,545 pothole repairs, and the council states approximately
                20% of its reactive spend goes on potholes every year. Reactive maintenance "may seem less expensive
                at first" but "can lead to higher costs due to frequent repairs" — the council's own words.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Best Practice AMBER weaken or strengthen the council's Section 58 defence?
              </h3>
              <p className="text-surface-600 text-sm">
                Cheshire East follows the Well Managed Highway Infrastructure code, uses asset-management-led
                programming, and has tested artificial intelligence to identify potholes — all of which support a
                documented Section 58 defence. But Best Practice is AMBER, not GREEN, and the council's 2024/25
                reactive share jumped to 25% of maintenance spending. Your claim still turns on the specific defect,
                prior reports, and photos — not the council's strategy documents alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Reactive spending rose to 25% in 2024/25 — what does that mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                For the four prior years, Cheshire East's reactive share sat at 15–18%. In 2024/25 it rose to 25% —
                the highest in the published five-year table — while pothole repairs hit a record 37,766. That is
                documented shift from prevention towards patching, which is useful context when arguing the council
                was fighting a losing reactive battle on the road where you were damaged.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.cheshireeast.gov.uk/highways_and_roads/road-maintenance/local-highways-maintenance-transparency-report/local-highways-maintenance-transparency-report.aspx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Cheshire East Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
